import { Check, ChevronDown, ChevronRight, Copy } from "lucide-react";
import React, { useCallback, useState } from "react";
import { graphql, useMutation } from "react-relay";
import config from "src/common/config";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "src/components/ui/dialog";
import GitLabIcon from "src/components/ui/icons/gitlab";
import { Input } from "src/components/ui/input";
import { Label } from "src/components/ui/label";
import { SecretInput } from "src/components/ui/secret-input";
import { Tooltip } from "src/components/ui/tooltip";
import { usePermissions } from "src/context/MeContext";
import { useToast } from "src/hooks/use-toast";
import { getProductName } from "src/utils/brandTheme";

import type { GitLabIntegrationCreateInstallationLinkMutation } from "./__generated__/GitLabIntegrationCreateInstallationLinkMutation.graphql";

interface GitLabIntegrationProps {
  isOpen: boolean;
  onClose: () => void;
  brandTheme?: string | null;
}

const createGitLabInstallationLinkMutation = graphql`
  mutation GitLabIntegrationCreateInstallationLinkMutation($input: CreateGitLabInstallationLinkInput!) {
    createGitlabInstallationLink(input: $input) {
      installationLink
    }
  }
`;

export default function GitLabIntegration({ isOpen, onClose, brandTheme }: GitLabIntegrationProps) {
  const productName = getProductName(brandTheme);
  const { canWrite } = usePermissions();
  const canModify = canWrite("integrations");
  const { toast } = useToast();

  const [instanceUrl, setInstanceUrl] = useState("");
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [isConnecting, setIsConnecting] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [callbackCopied, setCallbackCopied] = useState(false);
  const [scopeApi, setScopeApi] = useState(true);
  const [scopeWriteRepository, setScopeWriteRepository] = useState(true);

  const callbackUrl = `${config.get("APP_WWW_URL")}/gitlab/oauth/callback`;
  const handleCopyCallback = useCallback(async () => {
    await navigator.clipboard.writeText(callbackUrl);
    setCallbackCopied(true);
    setTimeout(() => setCallbackCopied(false), 2000);
  }, [callbackUrl]);

  const [commitCreateInstallationLink] = useMutation<GitLabIntegrationCreateInstallationLinkMutation>(
    createGitLabInstallationLinkMutation
  );

  const handleConnect = () => {
    const baseUrl = instanceUrl.replace(/\/+$/, "");

    if (!baseUrl) {
      toast({ title: "Instance URL is required", variant: "error" });
      return;
    }
    if (!clientId || !clientSecret) {
      toast({ title: "Client ID and Client Secret are required", variant: "error" });
      return;
    }

    const syncedResources = ["projects", "pipelines", "merge_requests"];

    const oauthScopes: string[] = [];
    if (scopeApi) oauthScopes.push("api");
    if (scopeWriteRepository) oauthScopes.push("write_repository");

    setIsConnecting(true);
    commitCreateInstallationLink({
      variables: {
        input: {
          baseUrl,
          clientId,
          clientSecret,
          syncedResources,
          oauthScopes,
        },
      },
      onCompleted: (response) => {
        setIsConnecting(false);
        const link = response.createGitlabInstallationLink?.installationLink;
        if (link) {
          window.location.href = link;
        } else {
          toast({
            title: "Failed to generate GitLab authorization link",
            description: "No link was returned from the server",
            variant: "error",
          });
        }
      },
      onError: (error) => {
        setIsConnecting(false);
        toast({
          title: "Failed to create GitLab connection",
          description: error.message,
          variant: "error",
        });
      },
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-2xl bg-popover text-foreground border-border max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <GitLabIcon className="h-6 w-6" />
            Connect GitLab
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            Connect {productName} to your self-hosted GitLab instance to analyze code, pipeline, and merge requests.
          </DialogDescription>
        </DialogHeader>

        <div className="overflow-y-auto flex-1 px-2 space-y-5 pt-2">
          {/* Setup instructions (collapsible) */}
          <div className="space-y-3">
            <button
              type="button"
              onClick={() => setShowInstructions(!showInstructions)}
              className="flex items-center gap-2 text-primary hover:text-primary/80 transition-colors"
            >
              {showInstructions ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
              {showInstructions ? "Hide instructions" : "Show instructions"}
            </button>

            <div className={`overflow-hidden transition-all duration-300 ease-in-out ${
              showInstructions
                ? "max-h-[2000px] opacity-100"
                : "max-h-0 opacity-0"
            }`}>
              <div className="text-muted-foreground space-y-3">
                <p className="mb-2 font-bold">GitLab Instance-Wide OAuth Application Setup:</p>
                <ol className="text-sm space-y-2 list-decimal list-inside">
                  <li>Go to your GitLab <span className="text-amber-600">Admin Area</span> (requires administrator access)</li>
                  <li>Navigate to <span className="text-amber-600">Applications</span></li>
                  <li>Click <span className="text-amber-600">New application</span> and set the redirect URI to: <code className="text-green-400 break-all">{callbackUrl}</code>
                    <button type="button" onClick={handleCopyCallback} className="inline-flex items-center ml-2 text-muted-foreground hover:text-foreground transition-colors">
                      {callbackCopied ? <Check className="h-3.5 w-3.5 text-green-400" /> : <Copy className="h-3.5 w-3.5" />}
                    </button>
                  </li>
                  <li>Select the scopes checked below in <span className="text-amber-600">Scopes Granted</span>, then save the application</li>
                  <li>Copy the Client ID and Client Secret below</li>
                </ol>
              </div>
            </div>
          </div>

          {/* Instance URL */}
          <div className="space-y-2">
            <Label className="text-sm font-medium text-muted-foreground">Instance URL</Label>
            <Input
              type="url"
              placeholder="https://gitlab.example.com"
              value={instanceUrl}
              onChange={(e) => setInstanceUrl(e.target.value)}
              className="bg-card border-border text-foreground placeholder:text-muted-foreground/50"
            />
          </div>

          {/* OAuth credentials - side by side */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-sm font-medium text-muted-foreground">Client ID</Label>
              <Input
                type="text"
                autoComplete="off"
                placeholder="OAuth Application Client ID"
                value={clientId}
                onChange={(e) => setClientId(e.target.value)}
                className="bg-card border-border text-foreground placeholder:text-muted-foreground/50"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium text-muted-foreground">Client Secret</Label>
              <SecretInput
                placeholder="OAuth Application Client Secret"
                value={clientSecret}
                onChange={(e) => setClientSecret(e.target.value)}
                className="bg-card border-border text-foreground placeholder:text-muted-foreground/50"
              />
            </div>
          </div>

          {/* Scopes Granted */}
          <div className="space-y-2">
            <Label className="text-sm font-medium text-muted-foreground">Scopes Granted</Label>
            <p className="text-xs text-muted-foreground">
              Ensure your GitLab OAuth application is configured with the scopes selected below.
            </p>
            <div className="grid grid-cols-2 gap-2 mt-1">
              <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-not-allowed">
                <input type="checkbox" checked disabled className="accent-primary" />
                <code className="text-green-400">read_api</code>
                <span className="text-xs">— read API access (required)</span>
              </label>
              <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-not-allowed">
                <input type="checkbox" checked disabled className="accent-primary" />
                <code className="text-green-400">read_repository</code>
                <span className="text-xs">— read code via Git (required)</span>
              </label>
              <label className="flex items-center gap-2 text-sm text-foreground cursor-pointer">
                <input
                  type="checkbox"
                  checked={scopeApi}
                  onChange={(e) => setScopeApi(e.target.checked)}
                  className="accent-primary"
                />
                <code className="text-green-400">api</code>
                <span className="text-xs text-muted-foreground">— full API access (merge requests)</span>
              </label>
              <label className="flex items-center gap-2 text-sm text-foreground cursor-pointer">
                <input
                  type="checkbox"
                  checked={scopeWriteRepository}
                  onChange={(e) => setScopeWriteRepository(e.target.checked)}
                  className="accent-primary"
                />
                <code className="text-green-400">write_repository</code>
                <span className="text-xs text-muted-foreground">— push code via Git</span>
              </label>
            </div>
          </div>

          {/* Connect button */}
          <Tooltip
            content={!canModify ? "You don't have permission to connect integrations" : null}
            position="top"
            asChild
          >
            <span className="block w-full">
              <button
                onClick={handleConnect}
                disabled={isConnecting || !canModify}
                className="w-full bg-[#24292f] text-white px-4 py-2 rounded-md flex items-center justify-center space-x-2 hover:bg-[#2d3339] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-[#24292f]"
              >
                <GitLabIcon className="h-5 w-5" />
                <span>{isConnecting ? "Loading..." : "Connect to GitLab"}</span>
              </button>
            </span>
          </Tooltip>
        </div>
      </DialogContent>
    </Dialog>
  );
}
